'use client';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { CldImage } from 'next-cloudinary';
import { useRouter } from 'next/navigation';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function PropertyImageSlider({
  images,
  propertyLink,
  height = 64,
  featured = false,
}: {
  images: string[];
  propertyLink?: string;
  height?: number;
  featured?: boolean;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const router = useRouter();
  const [isHovering, setIsHovering] = useState(false);

  const openProperty = () => {
    if (propertyLink) {
      router.push(propertyLink);
    }
  };

  const [current, setCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  // Use a taller image for featured properties
  const imageHeight = featured ? 80 : height;

  // Handle manual image navigation
  const handlePrev = () => {
    api?.scrollPrev();
  };

  const handleNext = () => {
    api?.scrollNext();
  };

  // Simulate image loading complete
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent"></div>

      {/* Image count indicator */}
      <div className="absolute bottom-3 left-3 bg-primary/70 text-white text-xs px-2 py-1 rounded-md z-10 flex items-center gap-1">
        <ImageIcon className="w-3 h-3" />
        <span>
          {current + 1}/{images.length}
        </span>
      </div>

      <Carousel setApi={setApi} className="w-full mx-auto rounded-t-3xl">
        <CarouselContent>
          {images &&
            images.map((image, index) => (
              <CarouselItem key={image + index} className="relative flex">
                {isLoading && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center z-10">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <CldImage
                  alt={`Property image ${index + 1}`}
                  width={300}
                  onClick={() => openProperty()}
                  height={featured ? 300 : 200}
                  className={cn(
                    'object-cover w-full cursor-pointer transition-all duration-500 ease-out rounded-t-3xl',
                    `h-${imageHeight}`,
                    featured ? 'hover:scale-105' : '',
                    isLoading ? 'opacity-0' : 'opacity-100'
                  )}
                  priority={index === 0}
                  src={image}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  sizes="(max-width: 640px) 100vw,
                  (max-width: 1024px) 90vw,
                  (max-width: 1280px) 80vw,
                  1200px"
                  placeholder="blur"
                  blurDataURL={image}
                  onLoad={() => setIsLoading(false)}
                />
              </CarouselItem>
            ))}
        </CarouselContent>

        {/* Custom navigation buttons */}
        {current > 0 && (
          <button
            onClick={handlePrev}
            className={cn(
              'absolute z-10 top-1/2 -translate-y-1/2 left-2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-1 transition-all shadow-md',
              isHovering || featured ? 'opacity-80' : 'opacity-0',
              images.length <= 1 ? 'hidden' : ''
            )}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}

        {current < images.length - 1 && (
          <button
            onClick={handleNext}
            aria-label="Next image"
            className={cn(
              'absolute z-10 top-1/2 -translate-y-1/2 right-2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-1 transition-all shadow-md',
              isHovering || featured ? 'opacity-80' : 'opacity-0',
              images.length <= 1 ? 'hidden' : ''
            )}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </Carousel>

      {/* Enhanced image indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 px-2 z-10">
          {images.map((_, index) => (
            <div
              key={`indicator-${index}`}
              aria-label={`Go to image ${index + 1}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                api?.scrollTo(index);
              }}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300 cursor-pointer',
                current === index ? 'bg-white w-6' : 'bg-white/50 w-1.5 hover:bg-white/70',
                featured && current === index ? 'w-8' : ''
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
